/**
 * Otomatik kampanya hediye ürünü sepete ekleme/çıkarma kontrolü.
 * api-campaign-calculate section'ından gelen verilere göre hediye ürününü yönetir.
 *
 * Checkout kilidi: hediye mutasyonu (settle-döngüsü + kuyruklanan yeniden
 * değerlendirme) sürerken bu kontrol `isBusy()` üzerinden HPAutoBagControl'ün
 * paylaşılan checkout kilidine katılır — sepet sayfasındaki ödeme butonları
 * disable edilir ve cart formunun submit'i engellenir. İlk kontrol
 * sonuçlanınca `initialSettled` işaretlenir; main-cart.liquid'in parse-time
 * kilidi ancak her iki kontrol de settled olduğunda açılır.
 *
 * Checkout öncesi doğrulama (hp-checkout-preflight.js) sepeti değiştirdikten
 * sonra `settle()` ile hediye kurgusunun sonuçlanmasını bekler.
 *
 * @class HPAutoCampaignControl
 */
class HPAutoCampaignControl {
  /** @type {string} */
  static SECTION_ID = 'api-campaign-calculate';

  /** @type {string} */
  static DATA_ELEMENT_ID = 'api-campaign-calculate-data';

  /** @type {string} */
  static EVENT_NAME = 'cart:campaignCalculate';

  /** Hediye mutasyonu takılı kalırsa checkout kilidinin fail-open süresi (ms). */
  static GUARD_FAILSAFE_MS = 20000;

  /** İlk kontrol sonuçlanmazsa settled sayılacağı insurance süresi (ms). */
  static SETTLE_INSURANCE_MS = 20000;

  /** settle(): süren settle-döngüsünün bitmesi için en fazla bekleme (ms). */
  static SETTLE_WAIT_MS = 5000;

  /** @type {boolean} Bir hediye mutasyonu (ekle/çıkar/azalt) sürüyor mu. */
  static _giftMutationInFlight = false;

  /** @type {boolean} Mutasyon sırasında yeni bir değerlendirme talebi geldi mi. */
  static _giftRecheckPending = false;

  /** @type {number} Checkout kilidini tutan aktif iş sayısı. */
  static _guardHold = 0;

  /** @type {ReturnType<typeof setTimeout>|null} Kilit fail-open zamanlayıcısı. */
  static _guardFailsafeTimer = null;

  /** @type {boolean} İlk kontrol sonuçlandı mı (sticky; parse-time kilidin ön koşulu). */
  static initialSettled = false;

  /**
   * Kampanya hediyesi mutasyonu sürüyor mu? Paylaşılan checkout kilidi
   * (HPAutoBagControl.isAnyCartSyncBusy) bu metodu okur.
   *
   * @returns {boolean}
   */
  static isBusy() {
    return HPAutoCampaignControl._guardHold > 0;
  }

  /**
   * İlk kontrolü tamamlandı olarak işaretler (sticky).
   */
  static markSettled() {
    HPAutoCampaignControl.initialSettled = true;
  }

  /**
   * Paylaşılan checkout kilidini yeniden değerlendirir (bag control üzerinden).
   */
  static _syncGuards() {
    if (window.HPAutoBagControl && typeof window.HPAutoBagControl.syncCheckoutGuards === 'function') {
      window.HPAutoBagControl.syncCheckoutGuards();
    }
  }

  /**
   * Checkout kilidini alır; ilk alışta fail-open zamanlayıcısı kurulur.
   */
  static _acquireGuard() {
    HPAutoCampaignControl._guardHold += 1;
    if (HPAutoCampaignControl._guardHold === 1) {
      clearTimeout(HPAutoCampaignControl._guardFailsafeTimer);
      HPAutoCampaignControl._guardFailsafeTimer = setTimeout(() => {
        console.warn('hp-auto-campaign-control: Gift mutation stuck, releasing checkout guard');
        HPAutoCampaignControl._guardHold = 0;
        HPAutoCampaignControl.markSettled();
        HPAutoCampaignControl._syncGuards();
      }, HPAutoCampaignControl.GUARD_FAILSAFE_MS);
    }
    HPAutoCampaignControl._syncGuards();
  }

  /**
   * Checkout kilidini bırakır; tüm işler bitince settled işaretlenir.
   */
  static _releaseGuard() {
    HPAutoCampaignControl._guardHold = Math.max(0, HPAutoCampaignControl._guardHold - 1);
    if (HPAutoCampaignControl._guardHold === 0) {
      clearTimeout(HPAutoCampaignControl._guardFailsafeTimer);
      HPAutoCampaignControl._guardFailsafeTimer = null;
      HPAutoCampaignControl.markSettled();
    }
    HPAutoCampaignControl._syncGuards();
  }

  /**
   * api-campaign-calculate section HTML'inden JSON verisini parse eder.
   *
   * @param {string} sectionHtml - response.sections['api-campaign-calculate'] HTML string
   * @returns {Object|null} Parse edilmiş campaign verisi veya null
   */
  static parseCampaignCalculateData(sectionHtml) {
    if (!sectionHtml || typeof sectionHtml !== 'string') return null;
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(sectionHtml, 'text/html');
      const scriptEl = doc.getElementById(HPAutoCampaignControl.DATA_ELEMENT_ID);
      if (!scriptEl?.textContent) return null;
      return JSON.parse(scriptEl.textContent.trim());
    } catch (e) {
      console.warn('hp-auto-campaign-control: Failed to parse campaign calculate data', e);
      return null;
    }
  }

  static normalizeCampaigns(data) {
    if (!data) return [];
    // campaigns[] varsa (boş bile olsa) onu kullan; yoksa legacy tek-kök JSON
    if (Array.isArray(data.campaigns)) {
      return data.campaigns;
    }
    return [data];
  }

  /**
   * @param {Object} campaign
   * @returns {boolean}
   */
  static campaignNeedsMutation(campaign) {
    const { gift_variant_id, should_add, should_remove, should_reduce, gift_keys } = campaign;
    const hasGiftLines = Array.isArray(gift_keys) && gift_keys.length > 0;
    return (
      (should_add && gift_variant_id) ||
      ((should_remove || should_reduce) && hasGiftLines)
    );
  }

  /**
   * @param {Object|null} data
   * @returns {boolean}
   */
  static responseNeedsMutation(data) {
    return HPAutoCampaignControl.normalizeCampaigns(data).some((campaign) =>
      HPAutoCampaignControl.campaignNeedsMutation(campaign)
    );
  }

  /**
   * Section Rendering API ile api-campaign-calculate section'ını cart context'inde çeker.
   *
   * @returns {Promise<string|null>} Sayfa HTML (section içerir) veya null
   */
  static async fetchSectionHtml() {
    const cartUrl = window.routes?.cart_url || '/cart';
    const url = `${cartUrl}?section_id=${HPAutoCampaignControl.SECTION_ID}`;
    try {
      // Hesap sonucu giriş durumuna göre değişir (müşteri hedefi). Yanıtta ETag
      // olduğundan tarayıcı eski gövdeyi 304 ile yeniden kullanabilir; giriş /
      // çıkış sonrası bayat karar almamak için HTTP cache devre dışı bırakılır.
      const res = await fetch(url, { cache: 'no-store' });
      return res.ok ? res.text() : null;
    } catch (e) {
      console.warn('hp-auto-campaign-control: Failed to fetch section', e);
      return null;
    }
  }

  /**
   * Section HTML'den campaign verisini alır. Önce response'tan, yoksa Section Rendering API ile.
   *
   * @param {Object} response - Cart API response (sections key'i olabilir)
   * @returns {Promise<Object|null>} Campaign verisi veya null
   */
  static async getCampaignDataFromResponse(response) {
    let sectionHtml = response?.sections?.[HPAutoCampaignControl.SECTION_ID];

    if (!sectionHtml) {
      sectionHtml = await HPAutoCampaignControl.fetchSectionHtml();
    }

    return sectionHtml ? HPAutoCampaignControl.parseCampaignCalculateData(sectionHtml) : null;
  }

  /**
   * Cart API response'unda api-campaign-calculate varsa event tetikler.
   * Yoksa Section Rendering API ile section çekip event tetikler.
   *
   * @param {Object} response - Cart API response (sections key'i olabilir)
   */
  static async dispatchCampaignCalculateIfNeeded(response) {
    const campaignData = await HPAutoCampaignControl.getCampaignDataFromResponse(response);
    if (!campaignData) {
      // Veri alınamadıysa parse-time checkout kilidini asılı bırakma (fail-open).
      HPAutoCampaignControl.markSettled();
      HPAutoCampaignControl._syncGuards();
      return;
    }

    document.dispatchEvent(
      new CustomEvent(HPAutoCampaignControl.EVENT_NAME, { detail: campaignData })
    );
  }

  /**
   * Kampanya hediye ürününü sepete ekler.
   *
   * @param {number} giftVariantId - Hediye ürünü variant ID
   * @returns {Promise<void>}
   */
  static async addGiftToCart(giftVariantId) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        // Otomatik hediye ekleme müşterinin sepete ekleme aksiyonu değil;
        // RTB House basketadd event'i bu isteği atlar (rtb-house-events.js).
        'X-HP-No-RTB': '1',
      },
      body: JSON.stringify({
        items: [{ id: giftVariantId, quantity: 1 }],
      }),
    });

    // Stok tükenmesi gibi durumlarda /cart/add.js 422 döner; ürün eklenmez.
    if (!res.ok) return;

    document.dispatchEvent(new CustomEvent('cart:updated'));
  }

  /**
   * Hediye satır(lar)ını TEK atomik `/cart/update.js` çağrısıyla düzenler.
   * Hediye variant'ı birden fazla satıra dağılmış olabileceği için (tek satır
   * qty>1 VEYA harici indirim/cart-transform'un böldüğü çoklu satır) bütün
   * hediye line item key'leri tek seferde set edilir:
   *   - keepOne = false → tüm hediye satırları 0 (tamamen kaldır)
   *   - keepOne = true  → ilk satır 1, diğer tüm hediye satırları 0 (tek 1 adet)
   * Line item key kullanıldığından pozisyon bağımsızdır; yanlış satır silinmez.
   *
   * @param {string[]} giftKeys - Tüm hediye satırlarının line item key'leri
   * @param {boolean} keepOne - true: tek adet bırak, false: hepsini sil
   * @returns {Promise<void>}
   */
  static async setGiftLines(giftKeys, keepOne) {
    if (!Array.isArray(giftKeys) || giftKeys.length === 0) return;
    const updates = {};
    giftKeys.forEach((key, index) => {
      updates[key] = keepOne && index === 0 ? 1 : 0;
    });
    await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ updates }),
    });
    // Yeniden değerlendirme settle-döngüsü tarafından yapılır; burada yalnızca
    // sepet görünümünü tazelemek için cart:updated tetiklenir.
    document.dispatchEvent(new CustomEvent('cart:updated'));
  }

  /**
   * cart:campaignCalculate event'ini işler ve gerekli sepete ekleme/çıkarma işlemini yapar.
   *
   * @param {CustomEvent} event - cart:campaignCalculate event
   */
  static handleCampaignCalculate(event) {
    const campaigns = HPAutoCampaignControl.normalizeCampaigns(event.detail);
    const needsMutation = campaigns.some((campaign) =>
      HPAutoCampaignControl.campaignNeedsMutation(campaign)
    );

    if (!needsMutation) {
      // Sepet kampanya açısından tutarlı: parse-time checkout kilidinin
      // kampanya ayağı serbest bırakılabilir.
      HPAutoCampaignControl.markSettled();
      HPAutoCampaignControl._syncGuards();
      return;
    }

    // Aynı anda yalnızca bir settle-döngüsü çalışır. Döngü sürerken gelen yeni
    // kararlar işaretlenir ve döngü bitiminde bir kez daha değerlendirilir.
    if (HPAutoCampaignControl._giftMutationInFlight) {
      HPAutoCampaignControl._giftRecheckPending = true;
      return;
    }
    HPAutoCampaignControl._giftMutationInFlight = true;
    // Checkout kilidi: settle-döngüsü (ve kuyruklanan yeniden değerlendirme)
    // sonuçlanana kadar ödemeye geçiş kapalı tutulur.
    HPAutoCampaignControl._acquireGuard();

    HPAutoCampaignControl.runGiftSettleLoop()
      .catch((err) =>
        console.error('hp-auto-campaign-control: gift settle loop failed', err)
      )
      .finally(() => {
        HPAutoCampaignControl._giftMutationInFlight = false;
        if (HPAutoCampaignControl._giftRecheckPending) {
          HPAutoCampaignControl._giftRecheckPending = false;
          // Kilit, yeniden değerlendirme sonuçlanana kadar tutulur: dispatch
          // yeni bir mutasyon başlatırsa onun hold'u bizimkinden önce alınır
          // ve kilit hiç açılmadan devam eder (checkout'a boşluk kalmaz).
          HPAutoCampaignControl.dispatchCampaignCalculateIfNeeded({}).finally(() =>
            HPAutoCampaignControl._releaseGuard()
          );
        } else {
          HPAutoCampaignControl._releaseGuard();
        }
      });
  }

  /**
   * Sepet kararlı (mutasyon gerekmeyen) hale gelene kadar hediye ürününü
   * ekler/çıkarır/azaltır. HER adımda GÜNCEL sepet durumunu yeniden okur; bu
   * sayede eski (stale) bir snapshot'a göre yanlış/çift ekleme yapılmaz.
   * Kilit (`_giftMutationInFlight`) tüm döngü boyunca tutulur.
   *
   * @returns {Promise<void>}
   */
  static async runGiftSettleLoop() {
    for (let i = 0; i < 5; i++) {
      const data = await HPAutoCampaignControl.getCampaignDataFromResponse({});
      if (!data) return;

      const campaigns = HPAutoCampaignControl.normalizeCampaigns(data);
      const pendingCampaign = campaigns.find((campaign) =>
        HPAutoCampaignControl.campaignNeedsMutation(campaign)
      );
      if (!pendingCampaign) return;

      const { gift_variant_id, should_add, should_remove, gift_keys } = pendingCampaign;

      if (should_add && gift_variant_id) {
        await HPAutoCampaignControl.addGiftToCart(gift_variant_id);
      } else if (should_remove) {
        await HPAutoCampaignControl.setGiftLines(gift_keys, false);
      } else {
        await HPAutoCampaignControl.setGiftLines(gift_keys, true);
      }
    }
  }

  /**
   * Süren settle-döngüsünün bitmesini bekler.
   *
   * @param {number} timeoutMs
   * @returns {Promise<boolean>} Döngü yoksa/bittiyse true, süre dolduysa false
   */
  static async _waitForLoopIdle(timeoutMs) {
    const deadline = Date.now() + timeoutMs;
    while (HPAutoCampaignControl._giftMutationInFlight) {
      if (Date.now() >= deadline) return false;
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
    return true;
  }

  /**
   * Sepeti kampanya açısından tutarlı hale getirir ve bunun bitmesini bekler.
   * Checkout öncesi doğrulama (hp-checkout-preflight.js) stok düzeltmesinden
   * sonra hediye kurgusunu güncel sepetle yeniden değerlendirmek için kullanır.
   *
   * Aynı anda tek settle-döngüsü kuralı korunur: süren bir döngü varsa önce
   * onun bitmesi beklenir, ardından GÜNCEL sepetle döngü çalıştırılır. Döngü
   * sürerken gelen yeniden değerlendirme talepleri (_giftRecheckPending) bu
   * çağrı içinde karşılanır. Checkout kilidi çağrı boyunca tutulur.
   *
   * @returns {Promise<void>}
   */
  static async settle() {
    HPAutoCampaignControl._acquireGuard();
    try {
      for (let round = 0; round < 3; round++) {
        const idle = await HPAutoCampaignControl._waitForLoopIdle(HPAutoCampaignControl.SETTLE_WAIT_MS);
        // Süren döngü sonuçlanmadı (asılı fetch vb.): aynı anda ikinci döngü
        // çalıştırılmaz (fail-open).
        if (!idle) return;

        HPAutoCampaignControl._giftMutationInFlight = true;
        HPAutoCampaignControl._giftRecheckPending = false;
        try {
          await HPAutoCampaignControl.runGiftSettleLoop();
        } finally {
          HPAutoCampaignControl._giftMutationInFlight = false;
        }
        if (!HPAutoCampaignControl._giftRecheckPending) return;
      }
    } catch (err) {
      console.error('hp-auto-campaign-control: settle failed', err);
    } finally {
      HPAutoCampaignControl._releaseGuard();
    }
  }

  /**
   * Sayfa yüklendiğinde bir kez api-campaign-calculate render edip kontrol sağlar.
   */
  static async runInitialCheck() {
    const campaignData = await HPAutoCampaignControl.getCampaignDataFromResponse({});
    if (!campaignData) {
      // Kontrol yapılamadı: parse-time checkout kilidini asılı bırakma.
      HPAutoCampaignControl.markSettled();
      HPAutoCampaignControl._syncGuards();
      return;
    }

    document.dispatchEvent(
      new CustomEvent(HPAutoCampaignControl.EVENT_NAME, { detail: campaignData })
    );
  }

  /**
   * Event listener'ı kaydeder ve ilk kontrolü çalıştırır.
   */
  static init() {
    document.addEventListener(
      HPAutoCampaignControl.EVENT_NAME,
      HPAutoCampaignControl.handleCampaignCalculate
    );

    // bfcache'den dönüşte (ör. checkout'tan geri tuşu) durumu tekrar doğrula.
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) {
        HPAutoCampaignControl.runInitialCheck().catch((err) =>
          console.warn('hp-auto-campaign-control: pageshow check failed', err)
        );
      }
    });

    // Insurance: ilk kontrol her ne sebeple olursa olsun sonuçlanmazsa
    // (asılı fetch vb.) settled işaretle ki parse-time kilit kalıcı olmasın.
    setTimeout(() => {
      if (!HPAutoCampaignControl.initialSettled) {
        console.warn('hp-auto-campaign-control: Initial check never settled, failing open');
        HPAutoCampaignControl.markSettled();
        HPAutoCampaignControl._syncGuards();
      }
    }, HPAutoCampaignControl.SETTLE_INSURANCE_MS);

    HPAutoCampaignControl.runInitialCheck().catch((err) => {
      console.warn('hp-auto-campaign-control: Initial check failed', err);
      HPAutoCampaignControl.markSettled();
      HPAutoCampaignControl._syncGuards();
    });
  }
}

// Paylaşılan checkout kilidi HPAutoBagControl.isAnyCartSyncBusy içinden okur.
window.HPAutoCampaignControl = HPAutoCampaignControl;

window.dispatchCampaignCalculateIfNeeded = HPAutoCampaignControl.dispatchCampaignCalculateIfNeeded.bind(
  HPAutoCampaignControl
);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => HPAutoCampaignControl.init());
} else {
  HPAutoCampaignControl.init();
}
